/** Eligibility for the day-3+ activation notice (client-side, local calendar days). */
import { ONBOARDING_HINT_ACTIVATION_NOTICE } from '@/constants/onboardingHints';

export const ACTIVATION_NOTICE_MIN_ACCOUNT_DAY = 3;

/**
 * @param {unknown} val
 * @returns {Date | null}
 */
export function toDate(val) {
  if (!val) return null;
  const date =
    val instanceof Date ? val : val.toDate ? val.toDate() : new Date(val);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {Date} date
 * @returns {Date}
 */
export function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calendar day index since signup (day 1 on the local day of `createdAt`).
 * @param {unknown} createdAt
 * @param {Date} now
 * @returns {number}
 */
export function accountDaySinceCreated(createdAt, now) {
  const created = toDate(createdAt);
  if (!created) return 0;
  const start = startOfLocalDay(created);
  const current = startOfLocalDay(now);
  const diffMs = current.getTime() - start.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
}

/**
 * @param {import('@/types').Task[]} tasks
 * @returns {number}
 */
export function countCompletedTasks(tasks) {
  let count = 0;
  for (const task of tasks ?? []) {
    if (!task?.completed || task.archived) continue;
    if (!toDate(task.completedAt)) continue;
    count += 1;
  }
  return count;
}

/**
 * @param {Object} params
 * @param {unknown} params.createdAt
 * @param {Date} params.now
 * @param {number} params.completedCount
 * @param {string[]} [params.seenHints]
 * @returns {{ eligible: boolean; completedCount: number }}
 */
export function getActivationNoticeEligibility({
  createdAt,
  now,
  completedCount,
  seenHints = [],
}) {
  if (seenHints.includes(ONBOARDING_HINT_ACTIVATION_NOTICE)) {
    return { eligible: false, completedCount };
  }
  if (accountDaySinceCreated(createdAt, now) < ACTIVATION_NOTICE_MIN_ACCOUNT_DAY) {
    return { eligible: false, completedCount };
  }
  if (completedCount < 1) {
    return { eligible: false, completedCount };
  }
  return { eligible: true, completedCount };
}
